import Trans from 'next-translate/Trans';
import { useRouter } from 'next/router';
import { matchSorter } from 'match-sorter';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';

//utils
import { useMixPanelTrackLinks } from '@/utils/mixpanel/useMixPanelTrackLinks';

// Lib
import { getMainMenuSocialProfile } from '@/lib/menu/main';
import { INSTAGRAM } from '@/lib/socialProfile';
import mainMenuGroup from '@/lib/menu/mainGroup';
import { quickAccessSchedulerPost } from '@/lib/mixpanel/trackLinks';

// Routes
import { DISCOVER } from '@/routes/privateRoutes';

// Hooks
import useAcl from '@/hooks/user/useAcl';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';
import useFormatMenuLink from '@/utils/hooks/menu/useFormatMenuLink';
import { useNewFeatureNotifications } from '@/utils/hooks/user/useNewFeatureNotifications';

// Components
import Icon from '@/components/base/Icon';
import LinkBase from '@/components/base/LinkBase';
import MenuEntry from './MenuEntry';
import MenuPopover from './MenuPopover';
import Tooltip from '@/components/base/Tooltip';
import Sparkles from '@/components/effects/Sparkles';
import Shining from '@/components/effects/Shining';
import Badge from '@/components/base/Badge';
import MenuHomeItem from '@/components/composite/Menu/MenuHomeItem';
import Button from '@/components/base/Button';

// Types
import type { ComponentPropsWithoutRef } from 'react';
import type { LinkHrefType, MainMenuEntryType } from '@/lib/menu/main';

type PanelPropsType = {
    menu: MainMenuEntryType;
};

const hasAccess = (item: MainMenuEntryType, hasAcl: (locked: string) => boolean): boolean => {
    if (item.locked) {
        return hasAcl(item.locked);
    }

    return true;
};

const SubMenuItem = ({ subMenu }: { subMenu: MainMenuEntryType }) => {
    const { t } = useTranslation('commons');
    const { isNewFeatureOnPages } = useNewFeatureNotifications();

    return (
        <>
            <Icon
                icon={subMenu.icon}
                className={clsx('text-base text-icoBlue', {
                    'opacity-30': subMenu?.isComingSoon
                })}
            />
            <span
                className={clsx('px-3', {
                    'opacity-30': subMenu?.isComingSoon
                })}>
                {t(subMenu.name)}
            </span>
            {isNewFeatureOnPages([subMenu?.link, ...(subMenu.urlActiveMenu ?? [])]) ? (
                <span className="flex-1 pt-px">
                    <Shining className="rounded-2xl">
                        <Badge variant="new" />
                    </Shining>
                </span>
            ) : null}
            {subMenu?.isComingSoon ? (
                <span className="flex-1 pt-px">
                    <Badge variant="coming-soon" />
                </span>
            ) : null}
        </>
    );
};

const HasAccessItem = ({
    subMenu,
    formatItemLink,
    isSubMenuActive
}: {
    subMenu: MainMenuEntryType;
    formatItemLink: (arg: LinkHrefType) => LinkHrefType;
    isSubMenuActive: boolean;
}) => {
    return subMenu?.isComingSoon ? (
        <div className="flex cursor-not-allowed">
            <SubMenuItem subMenu={subMenu} />
        </div>
    ) : (
        <LinkBase
            href={formatItemLink(subMenu?.link)}
            className={clsx('flex transition duration-300 ease-in-out hover:text-icoBlue', {
                'text-icoBlue': isSubMenuActive
            })}>
            <SubMenuItem subMenu={subMenu} />
        </LinkBase>
    );
};

const Panel = ({ menu }: PanelPropsType) => {
    const { t } = useTranslation('commons');
    const { pathname } = useRouter();
    const urlWithoutParams = pathname.split('?')[0];
    const { hasAcl } = useAcl();
    const { formatItemLink } = useFormatMenuLink();

    //track quick access scheduler post
    useMixPanelTrackLinks(quickAccessSchedulerPost);

    return (
        <>
            <div className="flex w-full flex-row items-center justify-between space-x-8 px-5 pt-4">
                <span className="py-1">{t(menu.name)}</span>
                {menu.quickAccess ? (
                    <Button
                        href={menu.quickAccess.link}
                        id={menu.quickAccess?.id}
                        as={LinkBase}
                        size="xs"
                        variant="noBackground"
                        icon={menu.quickAccess.icon}
                        className="whitespace-nowrap !border">
                        {t(menu.quickAccess.label)}
                    </Button>
                ) : null}
            </div>
            <MenuPopover.Separator className="mb-3.5 mt-2.5" />
            <div className="space-y-3.5 pb-5">
                {menu.subMenuItems.map((subMenu, index) => {
                    if (subMenu.name === 'separator_content') {
                        return <MenuPopover.Separator key={index} />;
                    }
                    const isSubMenuActive =
                        subMenu.link === urlWithoutParams ||
                        (subMenu?.urlActiveMenu &&
                            subMenu?.urlActiveMenu?.includes(urlWithoutParams));

                    return (
                        <MenuPopover.Item key={subMenu.name}>
                            {hasAccess(subMenu, hasAcl) ? (
                                <HasAccessItem
                                    subMenu={subMenu}
                                    isSubMenuActive={isSubMenuActive}
                                    formatItemLink={formatItemLink}
                                />
                            ) : (
                                <Tooltip
                                    popperOptions={{
                                        //prevent tooltip to pop in the wrong place
                                        strategy: 'fixed'
                                    }}
                                    disabled={!subMenu.discoverId && !subMenu.lockedTooltip}
                                    content={
                                        <span className="w-10">
                                            {subMenu.lockedTooltip ? (
                                                <Trans
                                                    i18nKey={subMenu.lockedTooltip}
                                                    values={{
                                                        feature_name: t(subMenu.name)
                                                    }}
                                                    components={{
                                                        0: (
                                                            <LinkBase
                                                                href={formatItemLink(subMenu.link)}
                                                                className="text-icoBlue underline"
                                                            />
                                                        )
                                                    }}
                                                />
                                            ) : (
                                                <Trans
                                                    i18nKey={`commons:discover_light_version.only_available_for_link`}
                                                    values={{ feature_name: t(subMenu.name) }}
                                                    components={{
                                                        0: (
                                                            <LinkBase
                                                                key="purchase_slots"
                                                                href={{
                                                                    pathname: DISCOVER,
                                                                    query: {
                                                                        tab: subMenu.discoverId
                                                                    }
                                                                }}
                                                                className="text-icoBlue underline"
                                                            />
                                                        )
                                                    }}
                                                />
                                            )}
                                        </span>
                                    }
                                    position="right"
                                    interactive>
                                    <div
                                        className={clsx('flex text-icoGray-500', {
                                            'text-icoGray-500': isSubMenuActive
                                        })}>
                                        <LinkBase
                                            href={
                                                subMenu.discoverId
                                                    ? {
                                                          pathname: DISCOVER,
                                                          query: { tab: subMenu.discoverId }
                                                      }
                                                    : formatItemLink(subMenu.link)
                                            }>
                                            <Icon
                                                icon={subMenu.icon}
                                                className="text-base text-icoGray-500"
                                            />
                                            <span className="px-3">{t(subMenu.name)}</span>
                                            <Icon
                                                icon="locked"
                                                className="text-icoGray-600 rounded-full bg-gray-200 p-1 text-8 dark:bg-icoDarkMode-anthracite"
                                            />
                                        </LinkBase>
                                    </div>
                                </Tooltip>
                            )}
                        </MenuPopover.Item>
                    );
                })}
            </div>
        </>
    );
};

export type MainMenuItemPropsType = ComponentPropsWithoutRef<'div'> & {
    open?: boolean;
    item: MainMenuEntryType;
    isActive: boolean;
};

const Item = ({ open, item, isActive }: MainMenuItemPropsType) => {
    const { t } = useTranslation('commons');

    const { isNewFeatureOnPages } = useNewFeatureNotifications();
    const isNewFeatureItem = item?.subMenuItems
        ? item?.subMenuItems
              .map((subMenuItem) => [subMenuItem?.link, ...(subMenuItem?.urlActiveMenu ?? [])])
              .flat()
        : [item?.link, ...(item?.urlActiveMenu ?? [])];

    const isComingSoonFeature = item?.subMenuItems
        ? item?.subMenuItems.filter((subMenuItem) => subMenuItem?.isComingSoon)?.length > 0
        : item?.isComingSoon;

    return (
        <div className="relative flex w-full justify-center px-0.5">
            <MenuEntry name={t(item.name)}>
                {(item?.subMenuItems || item?.link) && isNewFeatureOnPages(isNewFeatureItem) ? (
                    <Sparkles>
                        <MenuEntry.Icon icon={item.icon} isOpen={open} isActive={isActive} />
                    </Sparkles>
                ) : (
                    <>
                        <MenuEntry.Icon icon={item.icon} isOpen={open} isActive={isActive} />
                        {isComingSoonFeature ? (
                            <div className="absolute -bottom-1.5 -right-1 flex items-center">
                                <Shining>
                                    <Badge variant="coming-soon" shorten />
                                </Shining>
                            </div>
                        ) : null}
                    </>
                )}
            </MenuEntry>
            {open ? (
                <span className="absolute right-0 top-1/2 h-8 w-0.5 -translate-y-1/2 bg-icoBlue" />
            ) : null}
        </div>
    );
};

type SingleMenuEntryProps = {
    hasAccess: boolean;
    item: MainMenuEntryType;
};

export const SingleMenuEntry = ({ item, hasAccess }: SingleMenuEntryProps) => {
    const { pathname } = useRouter();
    const { t } = useTranslation('commons');
    const { formatItemLink } = useFormatMenuLink();
    const { isNewFeatureOnPages } = useNewFeatureNotifications();
    const urlWithoutParams = pathname.split('?')[0];
    const isActive =
        item?.link === urlWithoutParams || item?.urlActiveMenu?.includes(urlWithoutParams);

    const getTooltipContent = () => {
        if (item.lockedTooltip) {
            return (
                <Trans
                    i18nKey={item.lockedTooltip}
                    values={{
                        feature_name: t(item.name)
                    }}
                    components={
                        item?.lockedLinks
                            ? item?.lockedLinks
                            : {
                                  0: (
                                      <LinkBase
                                          href={formatItemLink(item.link)}
                                          className="text-icoBlue underline"
                                      />
                                  )
                              }
                    }
                />
            );
        }

        return (
            <Trans
                i18nKey="commons:discover_light_version.only_available_for_link"
                values={{
                    feature_name: t(item.name),
                    feature_link: item?.link
                }}
                components={{
                    0: (
                        <LinkBase
                            href={{
                                pathname: DISCOVER,
                                query: { tab: item.discoverId }
                            }}
                            className="text-icoBlue underline"
                        />
                    )
                }}
            />
        );
    };

    if (hasAccess && !item?.disabled) {
        return (
            <MenuEntry
                name={t(item.name)}
                link={formatItemLink(item?.link)}
                isComingSoon={item?.isComingSoon}>
                {isNewFeatureOnPages([item?.link, ...(item?.urlActiveMenu ?? [])]) ? (
                    <Sparkles>
                        <MenuEntry.Icon icon={item.icon} isActive={isActive} />
                    </Sparkles>
                ) : (
                    <>
                        <MenuEntry.Icon
                            icon={item.icon}
                            isActive={isActive}
                            isComingSoon={item?.isComingSoon}
                        />
                        {item?.isComingSoon ? (
                            <div className="absolute -bottom-1.5 -right-1 flex items-center">
                                <Shining>
                                    <Badge variant="coming-soon" shorten />
                                </Shining>
                            </div>
                        ) : null}
                    </>
                )}
            </MenuEntry>
        );
    }

    if (item?.disabled) {
        return (
            <Tooltip
                popperOptions={{
                    //prevent tooltip to pop in the wrong place
                    strategy: 'fixed'
                }}
                content={<span className="w-full">{item?.disabledTooltip}</span>}
                position="right"
                interactive>
                <div>
                    <span className="group relative flex cursor-not-allowed leading-none">
                        <Icon
                            icon={item.icon}
                            className="text-xl text-icoGray-500 opacity-20 dark:text-icoDarkMode-wolf"
                        />
                    </span>
                </div>
            </Tooltip>
        );
    }

    return (
        <Tooltip
            popperOptions={{
                //prevent tooltip to pop in the wrong place
                strategy: 'fixed'
            }}
            disabled={!item.discoverId && !item.lockedTooltip}
            content={<span className="w-full">{getTooltipContent()}</span>}
            position="right"
            interactive>
            <div>
                <LinkBase
                    href={
                        item.discoverId
                            ? {
                                  pathname: DISCOVER,
                                  query: { tab: item.discoverId }
                              }
                            : formatItemLink(item.link)
                    }
                    className="group relative flex leading-none">
                    <Icon icon={item.icon} className="text-xl text-icoGray-500" />
                    <Icon
                        icon="locked"
                        className="text-icoGray-600 absolute -right-2 -top-2 rounded-full border-2 border-white bg-gray-200 p-0.5 text-8 text-icoGray-500 dark:border-icoDarkMode-anthracite dark:bg-icoDarkMode-anthracite"
                    />
                </LinkBase>
            </div>
        </Tooltip>
    );
};

const MenuMain = () => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const isGroup = useIsGroup();
    const { pathname } = useRouter();
    const { hasAcl } = useAcl();

    const shouldBeDisplayed = (item: MainMenuEntryType): boolean => {
        if (item.shouldBeDisplayed) {
            return hasAcl(item.shouldBeDisplayed);
        }
        return true;
    };

    const urlWithoutParams = pathname.split('?')[0];
    const network = activeSocialProfile?.social_network_type ?? INSTAGRAM;

    const getMainMenu = () => {
        if (isGroup) {
            return mainMenuGroup;
        }

        return getMainMenuSocialProfile(network);
    };

    const mainMenu = getMainMenu();

    return (
        <div className="scrollbar hidden w-full flex-col items-center space-y-7 overflow-x-hidden py-5.5 md:flex">
            <MenuHomeItem />
            {mainMenu.map((item) => {
                if (!shouldBeDisplayed(item)) {
                    return null;
                }

                if (item.subMenuItems && hasAccess(item, hasAcl)) {
                    const isActive =
                        matchSorter(item.subMenuItems, urlWithoutParams, {
                            keys: [
                                { threshold: matchSorter.rankings.EQUAL, key: 'link' },
                                { threshold: matchSorter.rankings.EQUAL, key: 'urlActiveMenu' }
                            ]
                        }).length > 0;

                    return (
                        <MenuPopover
                            panel={<MenuMain.Panel menu={item} />}
                            key={item.name}
                            panelClassName="!w-full !min-w-56">
                            <MenuMain.Item item={item} isActive={isActive} />
                        </MenuPopover>
                    );
                }

                return (
                    <div className="flex w-full justify-center" key={item.name}>
                        <SingleMenuEntry hasAccess={hasAccess(item, hasAcl)} item={item} />
                    </div>
                );
            })}
        </div>
    );
};

MenuMain.Panel = Panel;
MenuMain.Item = Item;

export default MenuMain;
